import * as React from 'react';
import {useTranslation} from 'react-i18next';
import {useVirtualizer} from '@tanstack/react-virtual';
import {Skeleton} from '../../skeleton';
import {ThreeDotsLoader} from '../../three-dots-loader';
import {cn} from '../../lib/cn';
import type {SelectOption} from '../../lib/selector-option';
import {DefaultOption, type MenuListProps} from '../_select-internals';
import type {SelectValue} from '../_select-internals';
import type {ReactNode} from 'react';
import {useInfiniteScrollContext} from './InfiniteScrollContext';

function VirtualMenuList<T, V extends SelectValue, L extends ReactNode>(
  props: MenuListProps<T, V, L>,
) {
  const {
    id,
    labelledBy,
    describedBy,
    getOptionId,
    options,
    selectedOptions,
    isMulti,
    highlightedIndex,
    onOptionHover,
    onOptionClick,
    disabled,
    isOptionDisabled,
    menuLoading,
    menuClassName,
    noOptionsMessage,
    components,
    inputValue = '',
    formatOptionLabel,
    isOptionSelected: isOptionSelectedProp,
    onMenuScrollToBottom,
  } = props;
  const {
    canLoadMore,
    isLoadingMore,
    loadMoreItems,
    loadingMoreText,
    itemHeight,
    listHeight,
    overscan,
    loadMoreThreshold,
  } = useInfiniteScrollContext();
  const Option = components?.Option ?? DefaultOption;
  const {t} = useTranslation();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const lastLoadMoreOptionsLengthRef = React.useRef<number | null>(null);
  const previousHighlightedIndexRef = React.useRef(-1);
  const showLoaderRow = Boolean(canLoadMore || isLoadingMore);
  const showMenuLoader = Boolean(menuLoading);
  const itemCount = showMenuLoader ? 0 : options.length + (showLoaderRow ? 1 : 0);

  const virtualizer = useVirtualizer({
    count: itemCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => itemHeight,
    overscan,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const measuredListHeight = Math.min(listHeight, Math.max(totalSize, itemHeight));
  const resolvedLoadingMoreText = loadingMoreText ?? t('loading_more');

  React.useEffect(() => {
    if (!canLoadMore || isLoadingMore || !loadMoreItems) return;
    if (virtualItems.length === 0) return;
    const lastItem = virtualItems[virtualItems.length - 1];
    if (!lastItem || lastItem.index < options.length - loadMoreThreshold) return;
    if (lastLoadMoreOptionsLengthRef.current === options.length) return;
    lastLoadMoreOptionsLengthRef.current = options.length;
    loadMoreItems();
  }, [
    virtualItems,
    options.length,
    canLoadMore,
    isLoadingMore,
    loadMoreItems,
    loadMoreThreshold,
  ]);

  React.useEffect(() => {
    const changed = previousHighlightedIndexRef.current !== highlightedIndex;
    previousHighlightedIndexRef.current = highlightedIndex;
    if (highlightedIndex < 0 || !changed) return;
    virtualizer.scrollToIndex(highlightedIndex, {align: 'auto'});
  }, [highlightedIndex, virtualizer]);

  const activeOptionId =
    highlightedIndex >= 0 ? getOptionId(highlightedIndex) : undefined;
  const lastOptionIndex = options.length - 1;
  const emptyMessage = noOptionsMessage?.() ?? t('no_options');
  const isOptionSelected = (option: SelectOption<T, V, L>) =>
    isOptionSelectedProp
      ? isOptionSelectedProp(option, selectedOptions)
      : selectedOptions.some(s => s.value === option.value);

  const wasAtBottomRef = React.useRef(false);
  const handleScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!onMenuScrollToBottom) return;
      const target = event.currentTarget;
      const atBottom = target.scrollHeight - target.scrollTop - target.clientHeight <= 1;
      if (atBottom && !wasAtBottomRef.current) {
        onMenuScrollToBottom(event);
      }
      wasAtBottomRef.current = atBottom;
    },
    [onMenuScrollToBottom],
  );

  if (options.length === 0 || showMenuLoader) {
    return (
      <div className="min-h-[75px] px-4 pb-[19px] pt-[17px]">
        {isLoadingMore || showMenuLoader ? (
          <div
            role="status"
            aria-busy="true"
            aria-live="polite"
            className="flex flex-col gap-2"
          >
            <span className="sr-only">
              {showMenuLoader ? t('loading') : resolvedLoadingMoreText}
            </span>
            {Array.from({length: 3}).map((_, index) => (
              <Skeleton
                key={index}
                className="h-10 w-full rounded-md"
                style={{animationDelay: `${index * 120}ms`, opacity: 1 - index * 0.2}}
              />
            ))}
          </div>
        ) : (
          <div className="flex min-h-[40px] items-center text-[16px] font-medium text-[var(--chekin-color-brand-navy)]">
            {emptyMessage}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-[75px] px-4 pb-[19px] pt-[17px]">
      <div
        ref={scrollRef}
        className={cn('overflow-y-auto', menuClassName)}
        style={{height: `${measuredListHeight}px`}}
        onScroll={onMenuScrollToBottom ? handleScroll : undefined}
      >
        <div
          id={id}
          role="listbox"
          tabIndex={-1}
          aria-labelledby={labelledBy}
          aria-describedby={describedBy}
          aria-activedescendant={activeOptionId}
          className="relative w-full"
          style={{height: `${totalSize}px`}}
        >
          {virtualItems.map(virtualItem => {
            const isLoaderRow = virtualItem.index >= options.length;
            const option = options[virtualItem.index] as
              | SelectOption<T, V, L>
              | undefined;
            const isSelected = !isLoaderRow && option ? isOptionSelected(option) : false;
            const isHighlighted = virtualItem.index === highlightedIndex;
            const optionDisabledFlag = Boolean(
              disabled || option?.isDisabled || (option && isOptionDisabled?.(option)),
            );
            const isLastOption =
              !isLoaderRow && virtualItem.index === lastOptionIndex && !canLoadMore;

            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                className="absolute left-0 top-0 w-full h-full"
                style={{
                  height: `${virtualItem.size}px`,
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                {isLoaderRow ? (
                  <div
                    role="status"
                    aria-busy="true"
                    aria-live="polite"
                    className="flex h-full items-center justify-center"
                  >
                    <span className="sr-only">
                      {showMenuLoader && !isLoadingMore
                        ? t('loading')
                        : resolvedLoadingMoreText}
                    </span>
                    <ThreeDotsLoader
                      height={24}
                      width={24}
                      color="var(--chekin-color-brand-blue)"
                    />
                  </div>
                ) : option ? (
                  <Option
                    id={getOptionId(virtualItem.index)}
                    option={option}
                    index={virtualItem.index}
                    isSelected={isSelected}
                    isHighlighted={isHighlighted}
                    isDisabled={optionDisabledFlag}
                    isLast={isLastOption}
                    isMulti={Boolean(isMulti)}
                    onClick={onOptionClick}
                    onHover={onOptionHover}
                    innerRef={() => undefined}
                    inputValue={inputValue}
                    selectedOptions={selectedOptions}
                    formatOptionLabel={formatOptionLabel}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export {VirtualMenuList};
